import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Agendamento } from "../../../services/Agendamento";
import { toast } from "react-toastify";
import {
  IAgendamentoDTO,
  IAgendamentoHorarioForm,
  IReagendamentoProps,
} from "../../../types/agendamento";
import { Loja } from "../../../services/Lojas";
import { addDays } from "date-fns";
import { useContextSite } from "../../../context/Context";
import { Delivery } from "../../../services/Delivery";
import { ISelectOptions } from "../../../types/inputs";
import { formatDateToISO, reverseToDateObject } from "../../../utils/dateTransform";
import { IItineranteDTO } from "../../../types/itinerante";
import { Itinerante } from "../../../services/Itinerante";
import dayjs from "dayjs";

export const useScheduleConfirmation = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [diasIndisponiveis, setDiasIndisponiveis] = useState<Date[]>([]);
  const { setIsLoad, isLoad } = useContextSite();
  const [date, setDate] = useState<Date>(null);
  const [horarios, setHorarios] = useState<ISelectOptions[]>(
    [] as ISelectOptions[]
  );
  const [searchParams] = useSearchParams();
  const itineranteId = searchParams.get("itineranteId");
  const [isOpen, setIsOpen] = useState(false);
  const reagendamento = searchParams.get("reagendamento");
  const [form, setForm] = useState<IAgendamentoHorarioForm>(
    {} as IAgendamentoHorarioForm
  );
  const [agendamento, setAgendamento] = useState<IAgendamentoDTO>(
    {} as IAgendamentoDTO
  );
  const [itinerante, setItinerante] = useState<IItineranteDTO>();

  function getDiasIndisponiveis(agendamento: IAgendamentoDTO) {
    setIsLoad(true);
    if (agendamento?.loja?.uuid) {
      Loja.getDiasIndisponiveis({ uuidLoja: agendamento?.loja?.uuid })
        .then(({ data }) => {
          const options = data.map((item) => addDays(new Date(item), 1));
          setDiasIndisponiveis(options);
        })
        .catch(
          ({
            response: {
              data: { mensagem },
            },
          }) => toast.error(mensagem)
        )
        .finally(() => setIsLoad(false));
      return;
    }

    Delivery.getDiasIndisponiveis({ uuidDelivery: agendamento?.delivery?.uuid, concessionaria: agendamento?.concessionaria })
      .then(({ data }) => {
        const options = data.map((item) => addDays(new Date(item), 1));
        setDiasIndisponiveis(options);
      })
      .catch(
        ({
          response: {
            data: { mensagem },
          },
        }) => toast.error(mensagem)
      )
      .finally(() => setIsLoad(false));
  }

  useEffect(() => {
    if (params?.uuidAgendamento) {
      setIsLoad(true);

      Agendamento.getById({ uuid: params?.uuidAgendamento })
        .then(({ data }) => {
          setAgendamento(data);
          if (!data?.itinerante?.uuid) {
            getDiasIndisponiveis(data);
          } else {
            setDate(reverseToDateObject(data?.itinerante?.dataRealizacao));
            setItinerante(data?.itinerante);
          }
        })
        .catch(
          ({
            response: {
              data: { mensagem },
            },
          }) => {
            toast.error(mensagem);
          }
        )
        .finally(() => {
          setIsLoad(false);
        });

      if (itineranteId) {
        Itinerante.byId(itineranteId)
          .then(({ data }) => {
            setDate(dayjs(data.dataRealizacao).toDate());
          })
          .catch(
            ({
              response: {
                data: { messagem },
              },
            }) => toast.error(messagem)
          );
      }
    }
  }, [params?.uuidAgendamento, itineranteId]);

  useEffect(() => {
    if (date) {
      setForm((prev) => ({ ...prev, horaAgendada: null }));
      const newDate = formatDateToISO(date);

      if (itineranteId) {
        Itinerante.horarios_disponiveis(itineranteId).then(({ data }) => {
          const options = data.map((item) => ({
            value: item,
            label: item,
            element: item,
          }));

          setHorarios(options);
        });

        return;
      }

      if (agendamento?.loja?.uuid) {
        Loja.getHorariosDisponiveis({
          uuidLoja: agendamento?.loja?.uuid,
          dataAgendamento: newDate,
        }).then(({ data }) => {
          const options = data.map((item) => ({
            value: item,
            label: item,
            element: item,
          }));

          setHorarios(options);
        });

        return;
      }

      if (agendamento?.delivery?.uuid) {
        Delivery.getHorariosDisponiveis({
          uuidDelivery: agendamento?.delivery?.uuid,
          dataAgendamento: newDate,
        }).then(({ data }) => {
          const options = data.map((item) => ({
            value: item,
            label: item,
            element: item,
          }));
          setHorarios(options);
        });
        return;
      }

      if (agendamento?.itinerante?.uuid) {
        Itinerante.horarios_disponiveis(agendamento?.itinerante?.uuid).then(
          ({ data }) => {
            const options = data.map((item) => ({
              value: item,
              label: item,
              element: item,
            }));
            setHorarios(options);
          }
        );
        return;
      }
    }
  }, [date]);

  function onSubmit(e: React.SyntheticEvent) {
    e.preventDefault();

    if (reagendamento) {
      setIsOpen(true);
      return;
    }

    setIsLoad(true);

    const PAYLOAD = {
      ...form,
      uuid: agendamento?.uuid,
    };

    Agendamento.definirHorario(PAYLOAD)
      .then(({ data }) => {
        navigate(`/agendamento/${data.uuid}/confirmar-agendamento`);
      })
      .catch(
        ({
          response: {
            data: { mensagem },
          },
        }) => {
          toast.error(mensagem);
        }
      )
      .finally(() => setIsLoad(false));
  }

  function handleReagendamento() {
    setIsLoad(true);

    const PAYLOAD: IReagendamentoProps = {
      ...agendamento,
      uuidAgendamento: agendamento?.uuid,
      uuidLoja: agendamento?.loja?.uuid,
      uuidDelivery: agendamento?.delivery?.uuid,
      uuidItinerante: itineranteId,
      diaAgendado: itinerante != null ? itinerante?.dataRealizacao : form?.diaAgendado != null ? form?.diaAgendado : agendamento?.diaAgendado,
      horaAgendada: form.horaAgendada,
    };

    Agendamento.reagendar(PAYLOAD)
      .then(({ data }) => {
        navigate(`/meus-agendamentos/agendamento?id=${data.uuid}`);
      })
      .catch(
        ({
          response: {
            data: { mensagem },
          },
        }) => toast.error(mensagem)
      )
      .finally(() => {
        setIsLoad(false);
        setIsOpen(false);
      });
  }

  return {
    diasIndisponiveis,
    date,
    setDate,
    horarios,
    form,
    setForm,
    onSubmit,
    isOpen,
    setIsOpen,
    handleReagendamento,
    itinerante,
    isLoad,
    itineranteId,
  };
};
